import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "react-toastify";
import {
  DEFAULT_PER_PAGE,
  createInitialPagination,
} from "../../utils/pagination";

const MESSAGES = {
  fetchError: "企業一覧の取得に失敗しました。",
  saveSuccess: "企業担当者情報を更新しました。",
  saveError: "企業担当者情報の更新に失敗しました。",
  deleteSuccess: "企業を削除しました。",
  deleteError: "企業の削除に失敗しました。",
};

/**
 * CSRFトークンを取得する
 */
const getCsrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.content || "";

/**
 * 共通fetchオプションを作る
 */
const buildFetchOptions = ({
  method = "GET",
  body,
  signal,
  isJson = false,
  withCsrf = false,
} = {}) => {
  const headers = {
    Accept: "application/json",
  };

  if (isJson) {
    headers["Content-Type"] = "application/json";
  }

  if (withCsrf) {
    headers["X-CSRF-Token"] = getCsrfToken();
  }

  const options = {
    method,
    headers,
    credentials: "same-origin",
  };

  if (signal) {
    options.signal = signal;
  }

  if (body !== undefined) {
    options.body = isJson ? JSON.stringify(body) : body;
  }

  return options;
};

/**
 * フィールドエラーをフロント用に整形する
 */
const normalizeFieldErrors = (errors = {}) => {
  const normalized = {
    users: {},
  };

  Object.entries(errors).forEach(([key, messages]) => {
    const message = Array.isArray(messages) ? messages[0] : messages;

    const match = key.match(/^company_users\.(\d+)\.(\w+)$/);

    if (match) {
      const [, index, field] = match;

      normalized.users[index] = {
        ...(normalized.users[index] || {}),
        [field]: message,
      };

      return;
    }

    normalized[key.split(".").pop()] = message;
  });

  return normalized;
};

/**
 * APIエラーから表示用メッセージを取り出す
 */
const extractErrorMessage = async (res, fallbackMessage) => {
  try {
    const data = await res.json();

    return {
      message:
        (Array.isArray(data?.full_errors) && data.full_errors.join("\n")) ||
        data?.error ||
        fallbackMessage,
      fieldErrors: normalizeFieldErrors(data?.errors || {}),
    };
  } catch {
    return {
      message: fallbackMessage,
      fieldErrors: {},
    };
  }
};

/**
 * 企業一覧画面のロジックを管理するカスタムフック
 */
export default function useCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(
    createInitialPagination(DEFAULT_PER_PAGE),
  );

  const [showDialog, setShowDialog] = useState(false);

  // 企業削除用
  const [targetCompanyId, setTargetCompanyId] = useState(null);

  const abortRef = useRef(null);
  const requestIdRef = useRef(0);

  // 企業ごとのフィールドエラーを管理する状態
  const [fieldErrorsByCompany, setFieldErrorsByCompany] = useState({});

  // 企業ごとのフィールドエラーをクリアする
  const clearCompanyFieldErrors = useCallback((companyId) => {
    setFieldErrorsByCompany((prev) => ({
      ...prev,
      [companyId]: {},
    }));
  }, []);

  /**
   * 企業一覧を取得する
   */
  const fetchCompanies = useCallback(async (page = 1) => {
    if (abortRef.current) abortRef.current.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setLoading(true);

    try {
      const res = await fetch(
        `/admin/companies.json?page=${page}`,
        buildFetchOptions({ signal: controller.signal }),
      );

      if (!res.ok) throw new Error(MESSAGES.fetchError);

      const data = await res.json();

      if (requestId !== requestIdRef.current) return;

      setCompanies(data.companies || []);
      setPagination(
        data.pagination || createInitialPagination(DEFAULT_PER_PAGE),
      );
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error(MESSAGES.fetchError);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * 企業担当者情報を更新する
   * 担当者の変更・削除はすべて保存時にまとめて反映する
   */
  const saveCompanyUsers = useCallback(
    async (companyId, payload) => {
      try {
        // 保存前に前回のエラーをクリア
        clearCompanyFieldErrors(companyId);

        const res = await fetch(
          `/admin/companies/${companyId}`,
          buildFetchOptions({
            method: "PATCH",
            isJson: true,
            withCsrf: true,
            body: { company: payload },
          }),
        );

        if (!res.ok) {
          const { message, fieldErrors: newFieldErrors } =
            await extractErrorMessage(res, MESSAGES.saveError);

          // 企業ごとのフィールドエラーを更新
          setFieldErrorsByCompany((prev) => ({
            ...prev,
            [companyId]: newFieldErrors,
          }));

          throw new Error(message);
        }

        toast.success(MESSAGES.saveSuccess);
        await fetchCompanies(pagination?.current_page || 1);
        return true;
      } catch (error) {
        toast.error(error.message || MESSAGES.saveError, {
          style: { whiteSpace: "pre-line" },
        });
        return false;
      }
    },
    [clearCompanyFieldErrors, fetchCompanies, pagination],
  );

  /**
   * 企業削除確認ダイアログを開く
   */
  const openDeleteDialog = useCallback((companyId) => {
    setTargetCompanyId(companyId);
    setShowDialog(true);
  }, []);

  /**
   * 削除ダイアログを閉じる
   */
  const closeDeleteDialog = useCallback(() => {
    setShowDialog(false);
    setTargetCompanyId(null);
  }, []);

  /**
   * 企業を削除する
   */
  const deleteCompany = useCallback(async () => {
    if (!targetCompanyId) return;

    try {
      const res = await fetch(
        `/admin/companies/${targetCompanyId}`,
        buildFetchOptions({
          method: "DELETE",
          withCsrf: true,
        }),
      );

      if (!res.ok) {
        const { message } = await extractErrorMessage(
          res,
          MESSAGES.deleteError,
        );
        throw new Error(message);
      }

      toast.success(MESSAGES.deleteSuccess);
      closeDeleteDialog();
      await fetchCompanies(pagination?.current_page || 1);
    } catch (error) {
      toast.error(error.message || MESSAGES.deleteError, {
        style: { whiteSpace: "pre-line" },
      });
    }
  }, [targetCompanyId, closeDeleteDialog, fetchCompanies, pagination]);

  useEffect(() => {
    fetchCompanies(1);

    return () => {
      abortRef.current?.abort();
    };
  }, [fetchCompanies]);

  return {
    companies,
    loading,
    pagination,
    showDialog,
    fetchCompanies,
    saveCompanyUsers,
    openDeleteDialog,
    closeDeleteDialog,
    deleteCompany,
    fieldErrorsByCompany,
    clearCompanyFieldErrors,
  };
}
